import type { GetServerSideProps } from 'next'
import Bugsnag, { NotifiableError } from '@bugsnag/js'
import { getConfig } from './api/config/[campaign_id]'
import { getStudioCampaign } from './api/studio-campaigns/[id]'
import { Config } from 'modules/config/state/config-types'
import { URLParams } from 'modules/url-params/state/app-url-params-types'
import { ScriptTags } from 'modules/script-tags'
import { parseRawURLParams } from 'modules/url-params/utils/url-params-utils'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import { OptimizelyContext } from 'modules/optimizely/OptimizelyContext'
import OptimizelySDK from 'modules/optimizely/OptimizelySDK'
import { nanoid } from '@reduxjs/toolkit'
import { OptimizelyContextType } from 'models/Optimizely'

// Import the supported locales from next-i18next config
import nextI18NextConfig from '../../next-i18next.config'

import AppWrapperNoSSR from 'modules/app/app-wrapper-no-ssr'

interface EmbeddedPageProps {
  env?: string
  nonce: string
  config?: Config
  urlParams: URLParams
  optimizelyContext: OptimizelyContextType
}

const EmbeddedPage = ({ env, nonce, config, urlParams, optimizelyContext }: EmbeddedPageProps) => {
  const { t } = useTranslation()

  Bugsnag.notify('bugsnag test error')

  /**
   * If unable to find campaign for given cid and its respective oid, stop short
   */
  if (!env || !config) {
    console.log('Unable to fetch config to initialize the application')

    const error = new Error('Unable to fetch config to initialize the application')

    Bugsnag.notify(error, (event) => {
      event.addMetadata('env', { env })
      event.addMetadata('config', { config })
    })

    return <div>{t('index.errorStateNeeded', 'ErrorStateNeeded')}</div>
  }

  return (
    <OptimizelyContext.Provider value={optimizelyContext}>
      <AppWrapperNoSSR urlParams={urlParams} config={config} env={env} />
      <ScriptTags nonce={nonce} />
    </OptimizelyContext.Provider>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { query, locale } = context
  const env = process.env.APP_ENV as string
  const cid = context?.query?.cid as string
  const nonce = context.req.headers['x-nonce']

  let config
  let studioCampaign

  if (cid) {
    try {
      const configResponse = await getConfig(cid, 'published=1', context.req)
      studioCampaign = await getStudioCampaign(cid as string, 'published=1')

      if (configResponse.status === 404) {
        return {
          props: {
            env,
            nonce,
          },
        }
      }

      config = configResponse.data
    } catch (e) {
      console.error(e)
      Bugsnag.notify(e as NotifiableError)
    }
  }

  const urlParams = parseRawURLParams(query, config)

  // After parsing the URL params, set the query params on the context
  // This prevents conflicting URL params from being shown in the URL
  let newQuery: { [key: string]: string | undefined } = {
    ...query,
    frequency: query.frequency ? urlParams.frequency?.toString() : undefined,
    recurring: query.recurring ? urlParams.recurring?.toString() : undefined,
  }

  // Remove any undefined values from the newQuery object
  newQuery = Object.fromEntries(Object.entries(newQuery).filter(([_, value]) => !!value))

  // Redirect to the new URL with the updated query parameters
  if (JSON.stringify(newQuery) !== JSON.stringify(query)) {
    const newUrl = `${context.resolvedUrl.split('?')[0]}?${new URLSearchParams(
      newQuery as Record<string, string>,
    ).toString()}`

    return {
      redirect: {
        destination: newUrl,
        permanent: false, // Set to true if this is a permanent redirect
      },
    }
  }

  // Omit deeply nested properties from the config object that are undefined
  if (config) {
    config = JSON.parse(JSON.stringify(config))
  }

  // Get the supported locales from the config
  const supportedLocales = nextI18NextConfig.i18n.locales
  // Determine final locale with proper fallback logic:
  // 1) Use campaign default_locale if available and supported
  // 2) Use Next.js locale if supported
  // 3) Fallback to 'en'
  let finalLocale = 'en'

  if (studioCampaign?.default_locale && supportedLocales.includes(studioCampaign.default_locale)) {
    finalLocale = studioCampaign.default_locale
  } else if (locale && supportedLocales.includes(locale)) {
    finalLocale = locale
  }
  // Pass the finalLocale in the props

  const optimizelyUserId = nanoid()
  const optimizelyContext = await OptimizelySDK.getDecisions(optimizelyUserId)

  return {
    props: {
      env,
      nonce,
      config,
      urlParams,
      optimizelyContext,
      ...(await serverSideTranslations(finalLocale)),
    },
  }
}

export default EmbeddedPage
